import React from 'react';
import { StaticPageLayout } from '../StaticPageLayout';
import styles from '../StaticPageLayout.module.scss';
import { LAST_UPDATED_DATE } from '../../../utils/constants';
import { CONTACT_EMAIL, CONTACT_MAILTO, COMPANY_LEGAL_NAME } from '../../../utils/contactInfo';

export const PrivacyPolicy: React.FC = () => {
  return (
    <StaticPageLayout title="Privacy Policy">
      <div className={styles.contentWrapper}>
        <p>Last updated: {LAST_UPDATED_DATE}</p>

        <h2>1. Introduction</h2>
        <p>
          Welcome to MyMinu ("we," "our," or "us"). MyMinu is operated by {COMPANY_LEGAL_NAME}.
          We are committed to protecting your privacy and ensuring you have a positive experience
          on our website and in using our products and services (collectively, "Services").
        </p>

        <h2>2. Data We Collect</h2>
        <p>We collect the minimum data needed to provide the Services. This includes:</p>
        <ul>
          <li>Account information (email address, password hash)</li>
          <li>Profile preferences (theme, accessibility settings)</li>
          <li>Routine data (tasks, schedules, profile names you create)</li>
          <li>Diagnostic data (anonymous error logs and basic device info)</li>
        </ul>
        <p>
          <strong>Children's data:</strong> MyMinu is intended for use by parents and educators
          on behalf of children. We do not knowingly collect personal data directly from children
          under 16. Routine and profile names referring to a child are managed under the parent's
          account and are treated as personal data of the parent.
        </p>

        <h2>3. Legal Basis (GDPR)</h2>
        <p>
          We process personal data on the following legal bases under the EU General Data Protection
          Regulation (GDPR):
        </p>
        <ul>
          <li><strong>Contract</strong> — to provide the Services you sign up for.</li>
          <li><strong>Legitimate interest</strong> — to keep the Services secure and to fix bugs.</li>
          <li><strong>Consent</strong> — for any optional analytics or marketing communications.</li>
        </ul>

        <h2>4. How We Use Your Data</h2>
        <ul>
          <li>To provide, maintain, and improve the Service</li>
          <li>To notify you about important changes to the Service</li>
          <li>To respond to support requests</li>
          <li>To detect, investigate, and prevent abuse and security incidents</li>
        </ul>

        <h2>5. Data Sharing</h2>
        <p>
          We do not sell your personal data. We share data only with sub-processors that help us
          operate the Service (for example, our database and hosting providers), under contracts
          that meet GDPR requirements. A current list is available on request.
        </p>

        <h2>6. Data Retention</h2>
        <p>
          We retain account data for as long as your account is active. If you delete your account,
          we will delete or anonymise associated personal data within 30 days, except where we are
          required to keep records for legal or accounting reasons.
        </p>

        <h2>7. Your Rights</h2>
        <p>Under GDPR, you have the right to:</p>
        <ul>
          <li>Access the personal data we hold about you</li>
          <li>Have inaccurate data corrected</li>
          <li>Request deletion of your data ("right to be forgotten")</li>
          <li>Export your data in a portable format</li>
          <li>Object to or restrict certain processing</li>
          <li>Lodge a complaint with your local data protection authority</li>
        </ul>
        <p>
          To exercise any of these rights, email us at{' '}
          <a href={CONTACT_MAILTO}>{CONTACT_EMAIL}</a>.
        </p>

        <h2>8. Data Security</h2>
        <p>
          We use industry-standard security measures, including TLS encryption in transit and
          encryption at rest for sensitive fields. No system is perfectly secure, so we encourage
          you to use a strong, unique password.
        </p>

        <h2>9. International Transfers</h2>
        <p>
          MyMinu is hosted in the EU. If data is processed outside the EU/EEA, we use appropriate
          safeguards such as Standard Contractual Clauses.
        </p>

        <h2>10. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy occasionally. We will post the new version here with
          an updated "Last updated" date. Material changes will be communicated by email.
        </p>

        <h2>11. Contact Us</h2>
        <p>
          If you have any questions about this Privacy Policy, please contact us at{' '}
          <a href={CONTACT_MAILTO}>{CONTACT_EMAIL}</a>.
        </p>
      </div>
    </StaticPageLayout>
  );
};
